import { useAuth } from "@/context/AuthContext";
import { Lock, Sparkles } from "lucide-react";

/**
 * PremiumShieldGate
 * -----------------
 * Wraps a Premium Tax Shield-only widget. Renders the child normally for
 * subscribers on `premium_shield`; renders a frosted preview + upsell CTA
 * for anyone else. Zero server-side data leaks — the child never mounts
 * for non-eligible tiers.
 */
export function PremiumShieldGate({ label, tagline, children }) {
  const { user } = useAuth();
  const tier = String(user?.subscription_tier || "").toLowerCase();
  // Admins and enterprise_fleet accounts bypass the gate unconditionally.
  if (tier === "premium_shield" || tier === "enterprise_fleet" || user?.is_admin) return children;
  return (
    <div
      data-testid="premium-shield-gate"
      data-user-tier={tier || "pro"}
      className="relative rounded-2xl overflow-hidden"
      style={{
        background: "linear-gradient(180deg, rgba(15,23,42,0.85), rgba(30,15,60,0.85))",
        border: "1px solid rgba(168,85,247,0.4)",
        boxShadow: "0 20px 40px -20px rgba(168,85,247,0.45)",
        minHeight: 200,
      }}
    >
      {/* Static decorative preview — the real child is NOT mounted for non-eligible tiers,
          preventing accidental Premium-only API calls or heavy computation from leaking. */}
      <div
        aria-hidden="true"
        className="pointer-events-none select-none absolute inset-0 opacity-40"
        style={{ filter: "blur(6px) grayscale(0.4)" }}
      >
        <div className="p-6 space-y-3">
          <div className="h-3 w-2/3 rounded-full bg-purple-400/40" />
          <div className="h-3 w-1/2 rounded-full bg-purple-400/30" />
          <div className="h-24 rounded-xl bg-gradient-to-br from-purple-500/25 to-cyan-500/15 border border-purple-400/30" />
          <div className="h-3 w-3/4 rounded-full bg-purple-400/30" />
          <div className="h-3 w-1/3 rounded-full bg-purple-400/25" />
        </div>
      </div>
      <div className="relative z-10 flex flex-col items-center justify-center h-full p-6 text-center gap-3">
        <div
          className="w-12 h-12 rounded-full flex items-center justify-center"
          style={{
            background: "radial-gradient(circle, rgba(168,85,247,0.5), rgba(88,28,135,0.35))",
            border: "1px solid rgba(216,180,254,0.5)",
            boxShadow: "0 0 24px -4px rgba(168,85,247,0.65)",
          }}
        >
          <Lock size={20} className="text-purple-100" />
        </div>
        <div
          className="text-[10px] font-mono uppercase tracking-[0.28em] flex items-center gap-1.5"
          style={{ color: "#c4b5fd" }}
        >
          <Sparkles size={11} /> Premium Tax Shield
        </div>
        <div className="text-white text-lg font-bold leading-tight max-w-md">
          {label || "Locked · Available on Premium Tax Shield"}
        </div>
        {tagline && (
          <div className="text-slate-300 text-sm leading-relaxed max-w-md">{tagline}</div>
        )}
        <button
          onClick={() => window.location.assign("/paywall?upgrade=premium_shield_monthly")}
          data-testid="premium-shield-upsell-cta"
          className="mt-1 rounded-lg font-bold text-slate-950 text-sm uppercase tracking-widest transition-transform hover:-translate-y-px"
          style={{
            padding: "10px 22px",
            background: "linear-gradient(135deg, #a855f7, #06B6D4)",
            boxShadow: "0 12px 26px -10px rgba(168,85,247,0.7)",
            letterSpacing: "0.14em",
          }}
        >
          Unlock — $29.99/mo
        </button>
      </div>
    </div>
  );
}
